import { ConsentActionOutcomeV1, ConsentActionV1 } from '@csp/dmdp-api-consent-dto';
import { Maybe, RestOptions } from '@csp/csp-common-model';
import { filterMap } from '@csp/csp-common-util';
import { RoleType } from '@csp/dmdp-api-common-dto';
import { ConsentType } from '@csp/dmdp-api-user-dto';
import {
  ConsentFeatureTypeV1,
  ScheduledConsentActionStatusTypeV1,
  ScheduledConsentActionStatusV1,
  SkippedConsentInputV1,
} from '@csp/dto';
import { ConsentDocumentContentModel, ConsentTextContentModel } from '@csp/csp-pod-patient-content-definitions';
import { ZonedDateTimeFactory } from '@csp/csp-common-date-util';
import { sortBy } from 'lodash';
import { ConsentRestServiceV1 } from '@csp/dmdp-api-client';
import {
  ConsentActionOutcomeV1WithArtifactAndScheduleMeta,
  ConsentActionStatusValue,
  ConsentActionWithArtifactMeta,
} from '../../model/ConsentAction';
import { ConsentPlatform } from '../../model/ConsentPlatform';
import { calculateConsentActionStatus, transformConsentFeatureType } from '../../util/utils';
import { ScheduledConsent } from '../../model/ScheduledConsent';
import {
  DEFAULT_SCHEDULE_META_PROPERTY_DATA_PROCESSING,
  DEFAULT_SCHEDULE_META_PROPERTY_ENABLE_RECONSENT,
} from '../../mapper/defaultMetaProperties';
import { AppWebSystemTextIntersection } from '../../mapper/ConsentResponseMapper';
import { ScheduledConsentWithDocument } from '../../model/ScheduledConsentWithDocument';
import { addDocumentsToScheduledConsents } from './TextConsentContent';
import { mapToConsentActionOutcomeWithMeta } from './TextConsentMetaConfig';

const consentOrder: Array<ConsentType> = [
  ConsentType.PRIVACY_POLICY_EXTERNAL_SPONSOR,
  ConsentType.PRIVACY_POLICY,
  ConsentType.EULA,
  ConsentType.PRIVACY_POLICY_ZOOM,
  ConsentType.ANALYTICS,
  ConsentType.SPFQ_CONSENT,
  ConsentType.CSAT_CONSENT,
  ConsentType.UMUX_CONSENT,
];

export const getTextConsents = (
  unfilteredOutcomes: ConsentActionOutcomeV1[],
  consentActionsWithArtifactMeta: ConsentActionWithArtifactMeta[],
  platform: ConsentPlatform,
  legacyConsentTypes: ConsentType[],
  roles: RoleType[],
  onFeatures: Maybe<Array<ConsentFeatureTypeV1>>,
  consentTypesToFilterOut: Maybe<Array<string>>,
  skippedConsents: Array<SkippedConsentInputV1>,
  consentDocument: ConsentDocumentContentModel[],
  consentText: ConsentTextContentModel[],
  staticContent: AppWebSystemTextIntersection,
): ScheduledConsentWithDocument[] => {
  const scheduledConsents = toScheduledTextConsents(
    unfilteredOutcomes,
    consentActionsWithArtifactMeta,
    platform,
    legacyConsentTypes,
    roles,
    onFeatures,
    consentTypesToFilterOut,
    skippedConsents,
  );
  const scheduledConsentsWithDocuments = addDocumentsToScheduledConsents(
    scheduledConsents,
    consentDocument,
    staticContent,
    consentText,
  );

  return scheduledConsentsWithDocuments;
};

export const toScheduledTextConsents = (
  unfilteredOutcomes: ConsentActionOutcomeV1[],
  consentActionsWithArtifactMeta: ConsentActionWithArtifactMeta[],
  platform: ConsentPlatform,
  legacyConsentTypes: ConsentType[],
  roles: RoleType[],
  onFeatures: Maybe<Array<ConsentFeatureTypeV1>>,
  consentTypesToFilterOut: Maybe<Array<string>>,
  skippedConsents: Array<SkippedConsentInputV1>,
): ScheduledConsent[] => {
  const consentActionOutcomesWithArtifactAndScheduleMeta = filterMap(unfilteredOutcomes, outcome =>
    mapToConsentActionOutcomeWithMeta(
      outcome,
      platform,
      legacyConsentTypes,
      roles,
      onFeatures?.map(transformConsentFeatureType),
      consentTypesToFilterOut,
    ),
  );
  const latestConsentActionPerConsentType = groupLatestConsentActionByConsentType(consentActionsWithArtifactMeta);
  const latestOutcomePerConsentType = Object.values(
    groupLatestConsentActionByConsentType<ConsentActionOutcomeV1WithArtifactAndScheduleMeta>(
      consentActionOutcomesWithArtifactAndScheduleMeta,
    ),
  );
  const shouldPrompt = (consentType: string, consentActionStatus: ScheduledConsentActionStatusV1): boolean =>
    shouldPromptForTextConsent(consentType, consentActionStatus, latestConsentActionPerConsentType);

  const scheduledConsents = fromConsentActionOutcomeToSortedScheduledConsents(
    latestOutcomePerConsentType,
    skippedConsents,
    shouldPrompt,
  );

  return scheduledConsents;
};

export const getScheduledTextConsentWithConsentType = async (
  consentType: ConsentType,
  platform: ConsentPlatform,
  legacyConsentTypes: ConsentType[],
  roles: RoleType[],
  restOptions: RestOptions,
): Promise<ScheduledConsent | undefined> => {
  const outcomes = await ConsentRestServiceV1.getConsentActionOutcomesV1(restOptions);
  const filteredConsentsByConsentType = filterOutcomesWithConsentType(
    outcomes.consentActionOutcomes,
    consentType,
    platform,
    legacyConsentTypes,
    roles,
  );

  return filteredConsentsByConsentType;
};

const filterOutcomesWithConsentType = (
  outcomes: ConsentActionOutcomeV1[],
  consentType: ConsentType,
  platform: ConsentPlatform,
  legacyConsentTypes: ConsentType[],
  roles: RoleType[],
): ScheduledConsent | undefined => {
  const textConsents = toScheduledTextConsents(
    outcomes,
    [],
    platform,
    legacyConsentTypes,
    roles,
    undefined,
    undefined,
    [],
  );
  // There is only max 1 scheduled consent per consent type
  const filteredConsentsByConsentType = textConsents.find(textConsent => textConsent.consentType === consentType);

  return filteredConsentsByConsentType;
};

const shouldPromptForTextConsent = (
  consentType: string,
  consentActionStatus: ScheduledConsentActionStatusV1,
  latestConsentActionPerConsentTypeForPlatform: {
    [p: string]: ConsentActionV1;
  },
): boolean => {
  const latestActionForConsent = latestConsentActionPerConsentTypeForPlatform[consentType];
  const isLatestActionForConsentApproved = latestActionForConsent?.status?.value === ConsentActionStatusValue.APPROVED;

  return (
    consentActionStatus.status === ScheduledConsentActionStatusTypeV1.PENDING &&
    (latestActionForConsent ? isLatestActionForConsentApproved : true)
  );
};

export const groupLatestConsentActionByConsentType = <
  T extends ConsentActionOutcomeV1WithArtifactAndScheduleMeta | ConsentActionWithArtifactMeta,
>(
  outcomes: Array<T>,
): { [key: string]: T } => {
  const outcomeWithLatestWindowStartForConsentType = outcomes.reduce<{ [key: string]: T }>((acc, outcome) => {
    const consentType = outcome.artifactMeta.CONFIG_V2.cmsConsentType;

    const previousEntryForConsentType = Object.prototype.hasOwnProperty.call(acc, consentType)
      ? acc[consentType]
      : undefined;

    const currentLatestWindowStartForConsentType = ZonedDateTimeFactory.fromZonedDateTimeString(
      previousEntryForConsentType?.scheduleRequestReference?.windowStart,
    );
    const outcomeWindowStart = ZonedDateTimeFactory.fromZonedDateTimeString(
      outcome.scheduleRequestReference?.windowStart,
    );

    // If several consents with the same consent type are scheduled, pick the one with the latest windowStart
    const isOutcomeLaterThanCurrent =
      currentLatestWindowStartForConsentType === undefined ||
      (outcomeWindowStart && outcomeWindowStart.unixTimeMillis > currentLatestWindowStartForConsentType.unixTimeMillis);
    return isOutcomeLaterThanCurrent ? { ...acc, [consentType]: outcome } : acc;
  }, {});

  return outcomeWithLatestWindowStartForConsentType;
};

const fromConsentActionOutcomeToSortedScheduledConsents = (
  outcomes: Array<ConsentActionOutcomeV1WithArtifactAndScheduleMeta>,
  skippedConsents: Array<SkippedConsentInputV1>,
  shouldPrompt: (consentType: string, consentActionStatus: ScheduledConsentActionStatusV1) => boolean,
): Array<ScheduledConsent> => {
  const scheduledConsents = outcomes.map<ScheduledConsent>(outcome => {
    const skippedConsentMatch = skippedConsents.find(sc => sc.artifactCode === outcome.artifactCode);
    const artifactConfig = outcome.artifactMeta.CONFIG_V2;

    const {
      dataProcessing = DEFAULT_SCHEDULE_META_PROPERTY_DATA_PROCESSING,
      enableReconsent = DEFAULT_SCHEDULE_META_PROPERTY_ENABLE_RECONSENT,
      mandatory,
    } = outcome.scheduleMeta;
    const consentActionStatus = calculateConsentActionStatus(skippedConsentMatch, outcome.consentAction);

    return {
      id: outcome.artifactCode,
      artifact: {
        artifactCode: outcome.artifactCode,
      },
      artifactStatus: {
        id: outcome.artifactCode,
        actionStatus: consentActionStatus,
        shouldPrompt: shouldPrompt(artifactConfig.cmsConsentType, consentActionStatus),
      },
      consentActionStatus,
      mandatory,
      dataProcessing,
      enableReconsent,
      version: artifactConfig.version,
      consentType: artifactConfig.cmsConsentType,
      country: artifactConfig.country,
      documentClientType: artifactConfig.clientType,
    };
  });

  return sortBy(scheduledConsents, calculateOrderOfConsent);
};

const calculateOrderOfConsent = (scheduledConsent: ScheduledConsent): number => {
  const order = consentOrder.findIndex(consentType => consentType === scheduledConsent.consentType);

  return order > -1 ? order : Number.MAX_SAFE_INTEGER;
};

export const ExportsForTestTextConsent = {
  shouldPromptForTextConsent,
  fromConsentActionOutcomeToSortedScheduledConsents,
  calculateOrderOfConsent,
  filterOutcomesWithConsentType,
};
